import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axios from '../utils/api';
import { useAuth } from './AuthContext';

const WishlistContext = createContext();
const STORAGE_KEY = 'wishlist';

const readGuest = () => {
  try {
    const value = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
    return Array.isArray(value) ? value.map(String) : [];
  } catch {
    return [];
  }
};

export function WishlistProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [guestIdentifiers, setGuestIdentifiers] = useState(readGuest);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      setItems([]);
      setGuestIdentifiers(readGuest());
      return;
    }
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const guest = readGuest();
        const res = guest.length
          ? await axios.post('/api/users/wishlist/merge', { identifiers: guest })
          : await axios.get('/api/users/wishlist');
        if (active) setItems(Array.isArray(res.data) ? res.data : []);
        if (guest.length) localStorage.removeItem(STORAGE_KEY);
        if (active) setGuestIdentifiers([]);
      } catch (err) {
        console.log('Unable to load wishlist', err);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [user]);

  const identifiers = useMemo(
    () => user ? items.map(item => String(item._id)) : guestIdentifiers,
    [user, items, guestIdentifiers]
  );

  const isWishlisted = product => identifiers.includes(String(user ? product._id : (product._id || product.slug)))
    || (!user && identifiers.includes(String(product.slug)));

  const toggleWishlist = async product => {
    if (!user) {
      const id = String(product._id || product.slug);
      const next = guestIdentifiers.includes(id)
        ? guestIdentifiers.filter(value => value !== id)
        : [...guestIdentifiers, id];
      setGuestIdentifiers(next);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      return;
    }
    const exists = items.some(item => String(item._id) === String(product._id));
    const res = exists
      ? await axios.delete(`/api/users/wishlist/${product._id}`)
      : await axios.post(`/api/users/wishlist/${product._id}`);
    setItems(Array.isArray(res.data) ? res.data : []);
  };

  return (
    <WishlistContext.Provider value={{
      items, identifiers, count: user ? items.length : guestIdentifiers.length,
      loading, isWishlisted, toggleWishlist
    }}>
      {children}
    </WishlistContext.Provider>
  );
}

export const useWishlist = () => useContext(WishlistContext);
